import React, { useReducer } from 'react';
import WizardDialog from '../../../../components/envago/WizardDialog/WizardDialog';
import AccountShareJobs from '../AccountShareJobs/AccountShareJobs';
import Button from '../../../../components/envago/Button/Button';
import Information, { InformationType } from '../../../../components/envago/Information/Information';
import Collapsable from '../../../../components/envago/Collapsable/Collapsable';
import ColumnTreePicker from '../../../../components/envago/ColumnTreePicker/ColumnTreePicker';
import ExportJobSelectionDescription, { ExportJobSelectionNotice } from './ExportJobSelectionDescription';
import { JobType } from '../../../../redux/jobs/jobs.types';
import { ExcelExportFormatConfig } from '../../../../redux/application/application.types';
import { useSelector } from 'react-redux';
import { resolveExportConfig, AUTO_FORMAT_ID, CUSTOM_FORMAT_ID } from '../../../../services/excel/ExportConfigResolver';
import { buildSchema, createColumnLabelResolver } from '../../../../services/excel/ColumnSchemaBuilder';
import { filterColumnGroupsByAvailableValues } from '../../../../services/excel/ColumnGroups';
import { resolveLocalized } from '../../../../helper/LocalizedText';
import { buildWorkbook } from '../../../../services/excel/ExcelWorkbookBuilder';
import { download } from '../../../../services/excel/ExcelExportDownloader';
import dayjs from 'dayjs';
import classNames from 'classnames';
import LoadingOverlay from '../../../../components/envago/LoadingOverlay/LoadingOverlay';
import Checkbox from '../../../../components/envago/Checkbox/Checkbox';
import { useTranslation } from 'react-i18next';
import { isJobExpired } from '../../../../helper/ReadingDateRangeHelper';
import { mergeJobConfig } from '../../../../helper/JobConfigHelper';

interface ExportWizardDialogProps {
    visible: boolean;
    onClose: () => void;
    accountId?: string;
}

type WizardState = {
    step: 0 | 1;
    selectedJobs: JobType[];
    selectedGroups: { name: string; data: JobType[] }[];
    formatId: string;
    customColumns: string[];
    isLoading: boolean;
    error: boolean;
};

type WizardAction =
    | { type: 'SELECTION_CHANGED'; jobs: JobType[]; groups: { name: string; data: JobType[] }[] }
    | { type: 'NEXT' }
    | { type: 'BACK' }
    | { type: 'SET_FORMAT'; formatId: string }
    | { type: 'SET_CUSTOM_COLUMNS'; columns: string[] }
    | { type: 'EXPORT_START' }
    | { type: 'EXPORT_ERROR' }
    | { type: 'RESET' };

const initialState: WizardState = {
    step: 0,
    selectedJobs: [],
    selectedGroups: [],
    formatId: AUTO_FORMAT_ID,
    customColumns: [],
    isLoading: false,
    error: false,
};

const formatOptionClassName = (selected: boolean, className?: string) =>
    classNames(
        'flex items-start gap-2 w-full rounded-md border px-3 py-2.5 cursor-pointer transition-colors',
        selected ? 'border-primary bg-primary-50 text-primary' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-light',
        className,
    );

const formatLabelClassName = (selected: boolean) =>
    classNames('text-sm', {
        'font-semibold': selected,
        'font-medium': !selected,
    });

const waitForBrowserPaint = () =>
    new Promise<void>((resolve) => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                setTimeout(resolve, 0);
            });
            return;
        }

        setTimeout(resolve, 0);
    });

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
    switch (action.type) {
        case 'SELECTION_CHANGED':
            return { ...state, selectedJobs: action.jobs, selectedGroups: action.groups };
        case 'NEXT':
            return { ...state, step: 1, error: false };
        case 'BACK':
            return { ...state, step: 0, error: false, isLoading: false };
        case 'SET_FORMAT':
            return { ...state, formatId: action.formatId, error: false };
        case 'SET_CUSTOM_COLUMNS':
            return { ...state, customColumns: action.columns };
        case 'EXPORT_START':
            return { ...state, isLoading: true, error: false };
        case 'EXPORT_ERROR':
            return { ...state, isLoading: false, error: true };
        case 'RESET':
            return { ...initialState };
        default:
            return state;
    }
}

const ExportWizardDialog = ({ visible, onClose, accountId }: ExportWizardDialogProps) => {
    const [state, dispatch] = useReducer(wizardReducer, initialState);
    const [jobSelectionLayoutVersion, setJobSelectionLayoutVersion] = React.useState(0);
    const exportCancelled = React.useRef(false);
    const layoutRefreshFrame = React.useRef<number | null>(null);
    const { t, i18n } = useTranslation();

    const appConfig = useSelector((state: any) => state.application?.config ?? {});
    const configuredFormats = (appConfig as any)?.excelExport?.formats ?? [];

    const [showExpiredJobs, setShowExpiredJobs] = React.useState(false);
    const [showDoneJobs, setShowDoneJobs] = React.useState(false);

    // Per-job expiry check: honor a job-level override of the duration flag
    // (parity with the import side / JobDetails, which merge per job).
    const notExpiredFilter = React.useCallback(
        (job: JobType) => {
            const merged = mergeJobConfig(job.config, appConfig as any);
            const durationAllowed = merged?.features?.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed ?? null;
            return !isJobExpired(job, durationAllowed);
        },
        [appConfig],
    );
    const notDoneFilter = React.useCallback((job: JobType) => !job.done, []);
    const combinedFilter = React.useCallback(
        (job: JobType) => (showExpiredJobs || notExpiredFilter(job)) && (showDoneJobs || notDoneFilter(job)),
        [showExpiredJobs, showDoneJobs, notExpiredFilter, notDoneFilter],
    );
    const availableCustomColumnGroups = React.useMemo(() => filterColumnGroupsByAvailableValues(state.selectedJobs), [state.selectedJobs]);

    React.useEffect(() => {
        if (state.customColumns.length === 0) {
            return;
        }

        const availableKeys = new Set(availableCustomColumnGroups.flatMap((group) => group.columns.map((column) => column.key)));
        const nextColumns = state.customColumns.filter((key) => availableKeys.has(key));

        if (nextColumns.length !== state.customColumns.length) {
            dispatch({ type: 'SET_CUSTOM_COLUMNS', columns: nextColumns });
        }
    }, [availableCustomColumnGroups, state.customColumns]);

    const scheduleJobSelectionLayoutRefresh = React.useCallback(() => {
        if (layoutRefreshFrame.current !== null) {
            window.cancelAnimationFrame(layoutRefreshFrame.current);
        }

        layoutRefreshFrame.current = window.requestAnimationFrame(() => {
            layoutRefreshFrame.current = window.requestAnimationFrame(() => {
                layoutRefreshFrame.current = null;
                setJobSelectionLayoutVersion((version) => version + 1);
            });
        });
    }, []);

    React.useEffect(() => {
        if (!visible || state.step !== 0) {
            return;
        }

        scheduleJobSelectionLayoutRefresh();
        window.addEventListener('resize', scheduleJobSelectionLayoutRefresh);

        return () => {
            window.removeEventListener('resize', scheduleJobSelectionLayoutRefresh);

            if (layoutRefreshFrame.current !== null) {
                window.cancelAnimationFrame(layoutRefreshFrame.current);
                layoutRefreshFrame.current = null;
            }
        };
    }, [visible, state.step, scheduleJobSelectionLayoutRefresh]);

    React.useEffect(() => {
        if (!visible) {
            exportCancelled.current = true;
        }
    }, [visible]);

    // Reset only once the close transition has fully finished, so the user
    // never sees the dialog flash back to its initial state while closing.
    const handleClosed = () => {
        dispatch({ type: 'RESET' });
        setShowExpiredJobs(false);
        setShowDoneJobs(false);
    };

    const handleClose = () => {
        exportCancelled.current = true;
        onClose();
    };

    const handleExport = async () => {
        exportCancelled.current = false;
        dispatch({ type: 'EXPORT_START' });
        try {
            await waitForBrowserPaint();

            const hasSelectedGroups = state.selectedGroups.length > 0;
            const groups = hasSelectedGroups ? state.selectedGroups : [{ name: t('management.excelExport.wizard.defaultGroupName'), data: state.selectedJobs }];
            const usedNames = new Set<string>();

            const blobs = await Promise.all(
                groups.map(async (group) => {
                    const resolvedConfig = resolveExportConfig(
                        group.data,
                        state.formatId,
                        appConfig,
                        state.formatId === CUSTOM_FORMAT_ID ? state.customColumns : undefined,
                    );
                    const schema = buildSchema(group.data, resolvedConfig, createColumnLabelResolver(t, i18n.language));
                    const blob = await buildWorkbook(group.data, schema, resolvedConfig.features, appConfig as any);
                    const datePrefix = dayjs().format('YYYY-MM-DD');
                    const baseName = hasSelectedGroups ? `${datePrefix}-${group.name}` : `${datePrefix} - ${group.name}`;
                    let filename = `${baseName}.xlsx`;
                    let counter = 2;

                    while (usedNames.has(filename)) {
                        filename = `${baseName}-(${counter}).xlsx`;
                        counter++;
                    }

                    usedNames.add(filename);
                    return { name: filename, blob };
                }),
            );

            await download(blobs);
            if (!exportCancelled.current) {
                handleClose();
            }
        } catch (e) {
            if (!exportCancelled.current) {
                dispatch({ type: 'EXPORT_ERROR' });
            }
            console.error('ExportWizardDialog: export failed', e);
        }
    };

    const step1Footer = (
        <div className="flex justify-end gap-2 w-full">
            <Button ghost onClick={handleClose}>
                {t('management.excelExport.wizard.actions.cancel')}
            </Button>
            <Button primary disabled={state.selectedJobs.length === 0} onClick={() => dispatch({ type: 'NEXT' })}>
                {t('management.excelExport.wizard.actions.next')}
            </Button>
        </div>
    );

    const step2Footer = (
        <div className="flex justify-end gap-2 w-full">
            <Button ghost onClick={() => dispatch({ type: 'BACK' })} disabled={state.isLoading} className={state.isLoading ? 'opacity-50' : ''}>
                {t('management.excelExport.wizard.actions.back')}
            </Button>
            <Button primary onClick={handleExport} isLoading={state.isLoading}>
                {state.isLoading
                    ? t('management.excelExport.wizard.actions.loading')
                    : state.error
                    ? t('management.excelExport.wizard.actions.retry')
                    : t('management.excelExport.wizard.actions.export')}
            </Button>
        </div>
    );

    return (
        <WizardDialog
            title={t('management.excelExport.wizard.title')}
            steps={[t('management.excelExport.wizard.steps.selectJobs'), t('management.excelExport.wizard.steps.chooseFormat')]}
            currentStep={state.step}
            visible={visible}
            onClose={handleClose}
            onClosed={handleClosed}
            footer={state.step === 0 ? step1Footer : step2Footer}
            size={state.step === 0 ? 'wide' : 'default'}
            bodyClassName={state.step === 0 ? 'wizard-dialog--body-grow-content' : undefined}
        >
            <div data-export-wizard-layout-version={jobSelectionLayoutVersion} className={state.step !== 0 ? 'hidden' : 'flex flex-col h-full min-h-0'}>
                <div className="flex flex-col h-full min-h-0 w-full">
                    <AccountShareJobs
                        accountId={accountId}
                        description={<ExportJobSelectionDescription />}
                        notice={
                            <>
                                <ExportJobSelectionNotice />
                                <div className={'mt-2 flex gap-x-4 gap-y-2'}>
                                    <div>
                                        <Checkbox value={showDoneJobs} onChange={setShowDoneJobs} label={t('management.excelExport.wizard.showDoneJobs')} />
                                    </div>
                                    <div>
                                        <Checkbox
                                            value={showExpiredJobs}
                                            onChange={setShowExpiredJobs}
                                            label={t('management.excelExport.wizard.showExpiredJobs')}
                                        />
                                    </div>
                                </div>
                            </>
                        }
                        filterJob={combinedFilter}
                        selectionGroups="deepest"
                        onSelectionChanged={(_ids, jobs, groups) => {
                            dispatch({ type: 'SELECTION_CHANGED', jobs, groups });
                        }}
                    />
                </div>
            </div>

            <div className={state.step !== 1 ? 'hidden' : 'max-w-md w-full'}>
                <LoadingOverlay loading={state.isLoading} text={t('management.excelExport.wizard.loadingOverlay')} showSpinner={false}>
                    <div className="space-y-4">
                        {state.error && <Information type={InformationType.ERROR}>{t('management.excelExport.wizard.errors.exportFailed')}</Information>}

                        <div role="radiogroup" aria-label={t('management.excelExport.wizard.format.groupLabel')}>
                            <label className={formatOptionClassName(state.formatId === AUTO_FORMAT_ID, 'mb-4')}>
                                <input
                                    type="radio"
                                    aria-checked={state.formatId === AUTO_FORMAT_ID}
                                    checked={state.formatId === AUTO_FORMAT_ID}
                                    onChange={() => dispatch({ type: 'SET_FORMAT', formatId: AUTO_FORMAT_ID })}
                                    name="exportFormat"
                                    value={AUTO_FORMAT_ID}
                                    className="h-4 w-4 text-primary focus:ring-primary mt-0.5"
                                />
                                <span className="flex flex-1 flex-col">
                                    <span className={formatLabelClassName(state.formatId === AUTO_FORMAT_ID)}>
                                        {t('management.excelExport.wizard.format.auto')}
                                    </span>
                                    <span className="mt-0.5 text-xs text-gray-500 leading-snug">{t('management.excelExport.wizard.info.autoFormat')}</span>
                                </span>
                            </label>

                            <Collapsable
                                triggerText={<span className="text-sm font-medium text-gray-500">{t('management.excelExport.wizard.format.moreFormats')}</span>}
                                defaultOpen={false}
                                className="text-gray-500"
                            >
                                <div className="space-y-3 pt-3">
                                    {configuredFormats.map((format: ExcelExportFormatConfig) => {
                                        const selected = state.formatId === format.id;
                                        const label = resolveLocalized(format.label, i18n.language) ?? format.id;
                                        const description = resolveLocalized(format.description, i18n.language);

                                        return (
                                            <label key={format.id} className={formatOptionClassName(selected)}>
                                                <input
                                                    type="radio"
                                                    aria-checked={selected}
                                                    checked={selected}
                                                    onChange={() => dispatch({ type: 'SET_FORMAT', formatId: format.id })}
                                                    name="exportFormat"
                                                    value={format.id}
                                                    className="h-4 w-4 text-primary focus:ring-primary mt-0.5"
                                                />
                                                <span className="flex flex-1 flex-col">
                                                    <span className={formatLabelClassName(selected)}>{label}</span>
                                                    {description && <span className="mt-0.5 text-xs text-gray-500 leading-snug">{description}</span>}
                                                </span>
                                            </label>
                                        );
                                    })}

                                    <label className={formatOptionClassName(state.formatId === CUSTOM_FORMAT_ID)}>
                                        <input
                                            type="radio"
                                            aria-checked={state.formatId === CUSTOM_FORMAT_ID}
                                            checked={state.formatId === CUSTOM_FORMAT_ID}
                                            onChange={() => dispatch({ type: 'SET_FORMAT', formatId: CUSTOM_FORMAT_ID })}
                                            name="exportFormat"
                                            value={CUSTOM_FORMAT_ID}
                                            className="h-4 w-4 text-primary focus:ring-primary mt-0.5"
                                        />
                                        <span className="flex flex-1 flex-col">
                                            <span className={formatLabelClassName(state.formatId === CUSTOM_FORMAT_ID)}>
                                                {t('management.excelExport.wizard.format.custom')}
                                            </span>
                                            <span className="mt-0.5 text-xs text-gray-500 leading-snug">
                                                {t('management.excelExport.wizard.format.customHint')}
                                            </span>
                                        </span>
                                    </label>

                                    {state.formatId === CUSTOM_FORMAT_ID && (
                                        <div className="mt-2 space-y-3">
                                            <ColumnTreePicker
                                                groups={availableCustomColumnGroups}
                                                value={state.customColumns}
                                                onChange={(keys) => dispatch({ type: 'SET_CUSTOM_COLUMNS', columns: keys })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Collapsable>
                        </div>
                    </div>
                </LoadingOverlay>
            </div>
        </WizardDialog>
    );
};

export default ExportWizardDialog;
